import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Alert, Chip, Avatar,
  AppBar, Toolbar, IconButton, Tooltip, Snackbar, Rating,
  useMediaQuery, useTheme, Button,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ShareIcon from '@mui/icons-material/Share';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MapIcon from '@mui/icons-material/Map';
import ListIcon from '@mui/icons-material/ViewList';
import DirectionsIcon from '@mui/icons-material/Directions';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { getSharedList, getProgramPlacesByIds, getPersonalPlacesByIds, GOOGLE_MAPS_API_KEY } from '../../utils/firebase';
import { shareLink } from '../../utils/clipboard';
import { pinIcon, circleIcon, statusColor } from '../../utils/markers';
import { PROGRAM_META } from './programMeta';

const LIBRARIES = ['places'];
const SG_CENTER = { lat: 1.3521, lng: 103.8198 };
const MAP_OPTIONS = {
  mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
  styles: [
    { elementType: 'geometry', stylers: [{ color: '#1a1a2e' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#9fa8da' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#0d0d14' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#16213e' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0d1b2a' }] },
  ],
};

export function googleMapsUrl(place) {
  const query = encodeURIComponent([place.name, place.address].filter(Boolean).join(' '));
  const base = `https://www.google.com/maps/search/?api=1&query=${query}`;
  return place.placeId ? `${base}&query_place_id=${place.placeId}` : base;
}

export default function SharedListPage() {
  const { listId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [listMeta, setListMeta] = useState(null);
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [copied, setCopied] = useState(false);
  const [mobileView, setMobileView] = useState('list');
  const mapRef = useRef(null);
  const pendingFocusRef = useRef(null);

  const { isLoaded: mapsLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: LIBRARIES });

  useEffect(() => {
    async function load() {
      try {
        const list = await getSharedList(listId);
        if (!list) { setError('This list doesn’t exist or was deleted.'); setLoading(false); return; }
        setListMeta(list);

        const entries = list.places || [];
        // New lists embed full place snapshots; older lists only stored
        // {type, id} refs, so fall back to fetching those (program places are
        // public; personal ones may be unreadable and get skipped).
        const snapshots = entries.filter(p => p.name);
        const legacyPersonal = entries.filter(p => !p.name && p.type === 'personal').map(p => p.id);
        const legacyProgram = entries.filter(p => !p.name && p.type === 'program').map(p => p.id);

        const [personal, program] = await Promise.all([
          getPersonalPlacesByIds(legacyPersonal),
          getProgramPlacesByIds(legacyProgram),
        ]);
        setPlaces([
          ...snapshots.map(p => ({ ...p, _source: p.type })),
          ...personal.map(p => ({ ...p, _source: 'personal' })),
          ...program.map(p => ({ ...p, _source: 'program' })),
        ]);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [listId]);

  // Tab title matches the list, so shared links look right when opened
  useEffect(() => {
    if (listMeta?.name) document.title = `${listMeta.name} — FoodList`;
    return () => { document.title = 'FoodList'; };
  }, [listMeta]);

  const mapPlaces = places.filter(p => p.latitude && p.longitude);

  // Fit the map to the list's places once both are ready
  const fitBounds = (map) => {
    if (!map || mapPlaces.length === 0) return;
    if (mapPlaces.length === 1) {
      map.setCenter({ lat: mapPlaces[0].latitude, lng: mapPlaces[0].longitude });
      map.setZoom(15);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    mapPlaces.forEach(p => bounds.extend({ lat: p.latitude, lng: p.longitude }));
    map.fitBounds(bounds, 48);
  };

  const handleShare = async () => {
    const result = await shareLink(`${listMeta?.name || 'FoodList'} — FoodList`, window.location.href);
    if (result === 'copied') setCopied(true);
  };

  const handlePlaceClick = (place) => {
    setSelectedPlace(place);
    if (place.latitude && place.longitude) {
      if (mapRef.current) {
        mapRef.current.panTo({ lat: place.latitude, lng: place.longitude });
      } else {
        // Map not mounted yet (mobile list view) — focus it once it loads
        pendingFocusRef.current = place;
      }
      if (isMobile) setMobileView('map');
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', bgcolor: '#0d0d14' }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', bgcolor: '#0d0d14', p: 3, gap: 2 }}>
      <Typography sx={{ fontSize: '2.5rem' }}>🍽️</Typography>
      <Alert severity="error">{error}</Alert>
    </Box>
  );

  const listPanel = (
    <Box sx={{
      width: { xs: '100%', md: 380 }, flexShrink: 0, overflowY: 'auto',
      borderRight: { md: '1px solid rgba(255,255,255,0.06)' },
      p: 1.5, display: 'flex', flexDirection: 'column', gap: 1,
    }}>
      <Typography variant="caption" color="text.disabled" sx={{ px: 0.5 }}>
        {places.length} place{places.length !== 1 ? 's' : ''}
      </Typography>
      {places.map(place => (
        <SharedPlaceCard
          key={`${place._source}-${place.id}`}
          place={place}
          selected={selectedPlace?.id === place.id}
          onClick={() => handlePlaceClick(place)}
        />
      ))}
      {places.length === 0 && (
        <Typography color="text.disabled" variant="body2" sx={{ textAlign: 'center', pt: 4 }}>
          This list is empty.
        </Typography>
      )}
    </Box>
  );

  const mapPanel = (
    <Box sx={{ flex: 1, minHeight: 0 }}>
      {mapsLoaded ? (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={SG_CENTER}
          zoom={12}
          options={MAP_OPTIONS}
          onLoad={map => {
            mapRef.current = map;
            fitBounds(map);
            if (pendingFocusRef.current) {
              const p = pendingFocusRef.current;
              pendingFocusRef.current = null;
              map.setCenter({ lat: p.latitude, lng: p.longitude });
              map.setZoom(15);
            }
          }}
          onUnmount={() => { mapRef.current = null; }}
        >
          {mapPlaces.map(place => (
            <Marker
              key={`${place._source}-${place.id}`}
              position={{ lat: place.latitude, lng: place.longitude }}
              onClick={() => setSelectedPlace(place)}
              zIndex={selectedPlace?.id === place.id ? 1000 : undefined}
              icon={place._source === 'program'
                ? circleIcon(
                    PROGRAM_META[(place.programs || [])[0]]?.marker || '#42a5f5',
                    { selected: selectedPlace?.id === place.id },
                  )
                : pinIcon(statusColor(place), { selected: selectedPlace?.id === place.id })}
            />
          ))}
          {selectedPlace?.latitude && (
            <InfoWindow
              position={{ lat: selectedPlace.latitude, lng: selectedPlace.longitude }}
              onCloseClick={() => setSelectedPlace(null)}
            >
              <Box sx={{ maxWidth: 220, fontFamily: '"Manrope", sans-serif' }}>
                <Typography sx={{ fontWeight: 600, color: '#f2ede6', fontSize: 14, fontFamily: '"Fraunces", serif' }}>{selectedPlace.name}</Typography>
                {selectedPlace.cuisine && <Typography sx={{ color: '#b0a89e', fontSize: 11 }}>{selectedPlace.cuisine}</Typography>}
                {selectedPlace.address && <Typography sx={{ color: '#b0a89e', fontSize: 10, mt: 0.3 }}>{selectedPlace.address}</Typography>}
                <Box component="a" href={googleMapsUrl(selectedPlace)} target="_blank" rel="noopener"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.75, color: '#8ab4f8', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>
                  <DirectionsIcon sx={{ fontSize: 13 }} /> Open in Google Maps
                </Box>
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#0d0d14' }}>
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Toolbar sx={{ gap: 1 }}>
          <RestaurantIcon sx={{ color: '#ff7043', fontSize: 20 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap sx={{ lineHeight: 1.2 }}>{listMeta?.name}</Typography>
            {listMeta?.description && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{listMeta.description}</Typography>
            )}
          </Box>
          {isMobile && (
            <Button size="small" onClick={() => setMobileView(mobileView === 'list' ? 'map' : 'list')}
              startIcon={mobileView === 'list' ? <MapIcon /> : <ListIcon />}
              sx={{ color: 'text.secondary', flexShrink: 0 }}>
              {mobileView === 'list' ? 'Map' : 'List'}
            </Button>
          )}
          <Tooltip title="Copy link">
            <IconButton size="small" onClick={handleShare} sx={{ color: 'text.secondary' }}>
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {isMobile ? (mobileView === 'list' ? listPanel : mapPanel) : (
          <>
            {listPanel}
            {mapPanel}
          </>
        )}
      </Box>

      <Snackbar
        open={copied}
        autoHideDuration={2500}
        onClose={() => setCopied(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

function SharedPlaceCard({ place, selected, onClick }) {
  const programs = place.programs || [];
  return (
    <Box onClick={onClick} sx={{
      p: 1.5, borderRadius: 2, cursor: 'pointer',
      border: selected ? '1px solid rgba(255,112,67,0.5)' : '1px solid rgba(255,255,255,0.06)',
      bgcolor: selected ? 'rgba(255,112,67,0.08)' : 'rgba(255,255,255,0.02)',
      '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,112,67,0.3)' },
    }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Avatar src={place.photoURL || undefined} variant="rounded"
          sx={{ width: 48, height: 48, flexShrink: 0, bgcolor: 'rgba(255,112,67,0.15)', fontSize: '1.2rem', borderRadius: 1.5 }}>
          {!place.photoURL && '🍽️'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography noWrap sx={{ flex: 1, fontFamily: '"Fraunces", serif', fontWeight: 600, fontSize: '0.92rem' }}>{place.name}</Typography>
            {place.visited && <CheckCircleIcon sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0 }} />}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3, alignItems: 'center' }}>
            {place.cuisine && (
              <Chip label={place.cuisine} size="small"
                sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'rgba(255,255,255,0.06)', color: 'text.secondary' }} />
            )}
            {programs.map(p => (
              <Chip key={p} label={PROGRAM_META[p]?.short || p} size="small"
                sx={{ height: 16, fontSize: '0.6rem', bgcolor: PROGRAM_META[p]?.bg || 'rgba(255,255,255,0.1)', color: PROGRAM_META[p]?.color || '#fff' }} />
            ))}
            {place.visited && place.visitedRating != null && (
              <Rating value={place.visitedRating} readOnly size="small"
                sx={{ fontSize: '0.75rem', '& .MuiRating-iconFilled': { color: '#ff7043' } }} />
            )}
          </Box>
          {place.notes && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
              {place.notes}
            </Typography>
          )}
          {place.address && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
              <LocationOnIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.disabled" noWrap sx={{ flex: 1 }}>{place.address}</Typography>
            </Box>
          )}
          <Box component="a" href={googleMapsUrl(place)} target="_blank" rel="noopener"
            onClick={e => e.stopPropagation()}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, mt: 0.5, color: 'secondary.main', textDecoration: 'none', fontSize: '0.7rem', fontWeight: 600 }}>
            <DirectionsIcon sx={{ fontSize: 13 }} /> Google Maps
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
